import { useSyncExternalStore } from "react";

// Modo manutenção — fase 1, a metade automática (TUT-121).
//
// Durante um redeploy a API cai e volta sozinha; o frontend é estático e
// continua no ar. Sem isto, quem está logado vê o erro cru de rede. A ideia:
// quando uma chamada à API falha de um jeito que CHEIRA a queda (rede, 502,
// 503, 504), este módulo CONFIRMA contra `/api/health` — endpoint público e
// sem dependência de banco — antes de declarar manutenção. Sem a confirmação,
// o defeito de um endpoint específico (500 num handler) viraria "sistema em
// manutenção", que é exatamente a mentira que esconderia o defeito.
//
// Quem liga a suspeita é o `request()` do `api.ts` (todo caminho de chamada
// passa por lá); quem mostra a tela é o `App`, via `useManutencao()`; quem
// desliga é a `MaintenanceScreen`, sondando até a API voltar e recarregando a
// página — recarregar é de propósito: depois de um deploy os bundles mudaram,
// e continuar na árvore antiga rodaria código de outra versão.

let ativo = false;
let confirmando = false;
const ouvintes = new Set<() => void>();

function notificar() {
  for (const ouvinte of ouvintes) ouvinte();
}

export function manutencaoAtiva(): boolean {
  return ativo;
}

function assinar(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

export function useManutencao(): boolean {
  return useSyncExternalStore(assinar, manutencaoAtiva, manutencaoAtiva);
}

// A pergunta direta: a API está fora? `!ok` conta como fora porque durante o
// redeploy quem responde é o proxy (502/503), não a API.
export async function apiEstaFora(): Promise<boolean> {
  try {
    const resposta = await fetch("/api/health", {
      headers: { Accept: "application/json" },
      signal: AbortSignal.timeout(3000),
    });
    return !resposta.ok;
  } catch {
    return true;
  }
}

// Chamada pelo cliente HTTP quando uma falha cheira a queda. Confirma uma vez
// só (chamadas concorrentes não empilham sondas) e não reexamina depois de
// ativo — sair da manutenção é papel da tela, não daqui.
//
// TUT-472 (Q117) — no modo demonstração não há API atrás para estar fora: o
// `/api/health` não existe, a sonda sempre "confirmava" a queda, e uma falha
// de rede num dos poucos pedidos que o demo ainda faz (a carga comercial)
// trocava a tela inteira pela de manutenção. Lido na chamada, e não no topo
// do módulo, para o teste poder trocar o modo.
export async function reportarSuspeitaDeQueda(): Promise<void> {
  if (import.meta.env.VITE_API_MODE === "demo") return;
  if (ativo || confirmando) return;
  confirmando = true;
  try {
    if (await apiEstaFora()) {
      ativo = true;
      notificar();
    }
  } finally {
    confirmando = false;
  }
}

// Só para testes: o estado é de módulo e precisa voltar ao zero entre casos.
export function zerarManutencaoParaTeste(): void {
  ativo = false;
  confirmando = false;
}
